/**
 * Detailed Insights / blog teaser data for Quantum Star Marketing.
 *
 * Each entry is a fully-written long-form essay that opens in a modal.
 * Tone: operator-voice, opinionated, specific. No fake brand names.
 */
const INSIGHTS = [
  {
    key: "ai-shift-agencies",
    eyebrow: "AI / Growth",
    tag: "AI / Growth",
    name: "Why most agencies will not survive the AI shift",
    tagline: "And how to spot the ones that will.",
    overview:
      "The marketing agency model is being quietly dismantled by AI — and most agency owners don't realise how fast. The 40-person 'integrated' agency built on billable hours is structurally exposed: when one operator with the right AI stack can do the work of six, the labour-arbitrage business model collapses. This essay maps which agencies survive, which agencies die, and what brands should look for in 2026 partners.",
    highlights: [
      "The labour-arbitrage agency model is dead — it just hasn't fallen over yet",
      "AI doesn't replace creativity; it replaces middle-management coordination",
      "Survivors will be 4–12 person 'pod' agencies with proprietary AI layers",
      "Pricing shifts from time-based retainers to outcome-based fees",
      "Talent density beats headcount — one A-player + AI > five B-players",
      "Brands should ask: 'What does your AI stack do that mine cannot?'",
      "The agencies that survive will be smaller, more profitable, and rarer",
    ],
    highlightsLabel: "The argument",
    takeaways: [
      "If your agency is still charging by the hour, you're paying for inefficiency.",
      "Ask your agency to show you their AI stack — not slides, the actual systems.",
      "A 6-person elite pod will out-ship a 40-person traditional agency by month 3.",
      "Outcome-based pricing is a feature, not a discount — it forces alignment.",
      "The winner-takes-most dynamic that hit ecommerce + media is now hitting agencies.",
    ],
    takeawaysLabel: "Action items for brands",
    quote: {
      body: "Five years from now there will be two kinds of agencies: AI-native pods of 4–12 operators billing on outcomes, and museums.",
      attribution: "Internal Quantum Star ops memo, Jan 2026",
    },
    examples: [
      "Traditional 40-person Dubai agency — won pitches on relationships, lost 3 retainers in Q4 to 6-person pods",
      "MENA enterprise brand — replaced 4 agencies + 1 internal team with one AI-native pod, saved 38% on cost",
      "Founder-led agency — embraced AI tooling, doubled revenue per head in 18 months",
    ],
    examplesLabel: "Field signals (Q3–Q4 2025)",
    ctaPrimary: "Talk to a senior strategist →",
    ctaSecondary: "Run a Free AI Audit",
  },
  {
    key: "creative-testing-system",
    eyebrow: "Performance",
    tag: "Performance",
    name: "The 6-figure creative testing system we use on every Meta account above AED 500K/mo",
    tagline: "30+ ads a month, scientific selection, zero opinions.",
    overview:
      "Creative is 80% of Meta performance — but most agencies treat it like a vibe. We treat it like a science experiment. This is the exact playbook we run on every account spending AED 500K/month or more: how we brief creators, how many variants we ship, how we read results, what we kill, what we scale. The whole system is built to remove opinion from a process that should be 100% data-driven.",
    highlights: [
      "Brief structure: 1 hook variable + 1 angle variable + 3 format variations per cell",
      "30 ad variants shipped per month — minimum",
      "Two creator pods producing in parallel (statics + UGC + motion)",
      "Statistical confidence (95%+) before scaling decisions",
      "Cost-per-result + 7-day ROAS + thumb-stop rate as the holy trinity",
      "Kill switch: if a hook doesn't beat baseline in 3 days, it dies",
      "Winning hooks are translated into 3 new variants the next week",
    ],
    highlightsLabel: "The full system",
    takeaways: [
      "Stop testing 4 ads against each other — test 30 and let the data pick.",
      "Brief structure matters more than the creator. Bad brief = bad ad, every time.",
      "Statistical confidence is non-negotiable. Vibes-based scaling burns budget.",
      "Every winner becomes the seed for next week's variant batch.",
      "Without volume, you're not running a test — you're guessing.",
    ],
    takeawaysLabel: "Action items",
    quote: {
      body: "Most teams test 4 ads a month and wonder why scale plateaus. We test 30 and the math just works.",
      attribution: "Lead Paid Strategist, Quantum Star",
    },
    examples: [
      "UAE DTC fragrance — 32 ads/mo, identified hook that scaled spend 4x at flat ROAS",
      "GCC modest-fashion — UGC creator pod produced 28 variants/mo, ROAS lifted 2.1 → 4.6x",
      "MENA fitness app — discovered subtitled testimonial format outperformed founder pieces 3:1",
    ],
    examplesLabel: "Real campaigns the system has run",
    ctaPrimary: "Audit my Meta account →",
    ctaSecondary: "Book a Strategy Call",
  },
  {
    key: "plg-to-enterprise",
    eyebrow: "SaaS",
    tag: "SaaS",
    name: "From PLG to enterprise: building a compounding pipeline engine in 9 months",
    tagline: "What we learned scaling a MENA SaaS from AED 1.2M to AED 6.4M ARR.",
    overview:
      "PLG works for individual contributors. Enterprise sales works for buying committees. Most SaaS founders hit a wall trying to bridge the two. This essay walks through how we took a MENA-headquartered B2B SaaS from a self-serve $99/mo motion to a $40K+ annual contract motion in 9 months — without breaking the PLG funnel that was already working. The unlock was a parallel motion, not a replacement.",
    highlights: [
      "Run PLG and enterprise as parallel motions — not sequential",
      "PLG generates intent signal that enterprise sales hunts on top of",
      "Enterprise demo funnel is built around the buying committee, not the user",
      "ABM list of 50 named accounts beats inbound for ACVs above $20K",
      "Sales-assisted PLG (SLG) is the bridge between the two motions",
      "Pricing tiering is the highest-leverage decision you'll make",
      "Customer marketing > demand gen above $40K ACV — references close deals",
    ],
    highlightsLabel: "The playbook",
    takeaways: [
      "Don't kill your PLG funnel when you go upmarket — it becomes your intent layer.",
      "Build a named-account ABM motion alongside, with its own playbook.",
      "Pricing tiers should map to buying-committee triggers, not just feature gates.",
      "References are the unfair advantage in enterprise — invest in customer marketing early.",
      "9 months is realistic. 3 months is delusional.",
    ],
    takeawaysLabel: "Action items",
    quote: {
      body: "We almost killed our PLG funnel to focus on enterprise. Quantum Star's read was the opposite — run both. That decision tripled our pipeline.",
      attribution: "CEO, MENA logistics SaaS",
    },
    examples: [
      "MENA SaaS — ARR 1.2M → 6.4M AED in 9 months, with PLG funnel still growing 14% MoM",
      "GCC fintech B2B — added enterprise tier above existing self-serve, 11 deals in first quarter",
      "Vertical clinic SaaS — ABM motion on top-50 hospital groups landed 4 multi-site contracts",
    ],
    examplesLabel: "Reference engagements",
    ctaPrimary: "Book a SaaS Growth Audit →",
    ctaSecondary: "See our SaaS Service",
  },
  {
    key: "new-seo-playbook",
    eyebrow: "SEO",
    tag: "SEO",
    name: "The new SEO playbook: programmatic content + LLM-friendly entity architecture",
    tagline: "What works in 2026 when Google's SERPs are AI-rewritten.",
    overview:
      "The SEO playbook of 2020–2023 is dead. AI Overviews answer 60%+ of informational queries inside the SERP. Click-through rates on position-1 organic listings have halved. But SEO isn't dead — it's just changed. The brands winning in 2026 are running two things: programmatic content at industrial scale, and entity architecture optimised for how LLMs (Google's Gemini, ChatGPT, Perplexity) cite sources. This essay walks through both.",
    highlights: [
      "AI Overviews kill informational queries — chase commercial + transactional intent",
      "Programmatic content (1,000+ pages) covers long-tail at zero marginal cost",
      "Entity architecture: Schema.org + structured data + clean factual claims",
      "Become a citation source — LLMs cite from authoritative, structured sources",
      "Branded search is now your moat — invest in brand alongside SEO",
      "Internal linking + topical clusters matter more than backlinks for AI ranking",
      "EN + AR programmatic in parallel — GCC is one of the few growth SEO markets left",
    ],
    highlightsLabel: "The new playbook",
    takeaways: [
      "If your SEO strategy is still 'write 4 blog posts a month', you're losing.",
      "Programmatic content lets you cover 1,000 long-tail queries from one template.",
      "Entity architecture is the new backlink — get cited by LLMs, not just ranked.",
      "Brand searches now drive click-through more than position. Invest in brand.",
      "GCC SEO is still an arbitrage. EN + AR programmatic at scale wins.",
    ],
    takeawaysLabel: "Action items",
    quote: {
      body: "We've stopped thinking about Google rankings and started thinking about citation share — who do LLMs name when they answer questions about our category. That's the new SEO.",
      attribution: "Head of SEO, Quantum Star",
    },
    examples: [
      "MENA SaaS — 1,400 programmatic pages, ranking on 380, AI-citation appearances on 47",
      "Dubai-based clinic group — entity architecture got them cited in ChatGPT for dental queries",
      "UAE fintech — AR-language programmatic dominated Saudi long-tail, branded search up 280%",
    ],
    examplesLabel: "Real-world wins",
    ctaPrimary: "Get a Free SEO Audit →",
    ctaSecondary: "Talk to an SEO Strategist",
  },
];

export default INSIGHTS;
